/**
 * The tabs of the pages that put their content behind `?tab=`, and who may open
 * each one (z8nrz7f7g3).
 *
 * Shared by each page and the main menu, so the menu can never offer a tab the
 * page would refuse, or hide one the page shows. Pure on purpose: vitest is
 * node-only, so this is where the gates are tested.
 */

import { PERMISSIONS } from './permissions'
import { FEATURES, type FeatureKey } from './featureFlags'

export type PageTab<T extends string = string> = { value: T; label: string }

/** What a tab gate may read. Built by `useTabAccess`. */
export type TabAccess = {
  can: (key: string) => boolean
  isSuperAdmin: boolean
  /** Fails open while flags load, like `useFeatureFlag`. */
  featureOn: (key: FeatureKey) => boolean
}

// ─── Configure ───────────────────────────────────────────────────────────────

/** In tab-bar order. */
export const CONFIGURE_TABS = [
  { value: 'general', label: 'General' },
  { value: 'users', label: 'Users' },
  { value: 'roles', label: 'Roles & Permissions' },
  { value: 'tenants', label: 'Tenants' },
  { value: 'partners', label: 'Partners' },
  { value: 'user-groups', label: 'Email Groups' },
  { value: 'option-sets', label: 'Option Sets' },
  { value: 'feature-flags', label: 'Feature Flags' },
  // The ISBT 128 list, renamed from Products for the MES list beside it
  // (z8nrz7fvhx). The value stays, so links and saved tabs carry over.
  { value: 'products', label: 'Products CGT' },
  { value: 'product-code', label: 'Product Code' },
  { value: 'din-configure', label: 'DIN Configure' },
  { value: 'lis-stages', label: 'LIS Stages' },
  { value: 'language', label: 'Language & Terms' },
  { value: 'method', label: 'Method' },
  { value: 'purchase-orders', label: 'Purchase Orders' },
  { value: 'sso', label: 'SSO' },
] as const satisfies readonly PageTab[]

export type ConfigTab = (typeof CONFIGURE_TABS)[number]['value']

/**
 * Gate each tab by the routes its CONTROLS call, not by the topic it covers.
 *
 * Users, Roles and Feature Flags are superadmin-only because every route
 * behind them is global (no tenant guard): permissions resolve per tenant, so
 * a tenant role carrying e.g. users.manage would open a tab whose every
 * request 403s.
 *
 * Tenants is a MIXED surface and gating it as though it were global was a bug.
 * Tenant CRUD and the network rename are global, but a tenant's own member
 * management is deliberately delegable — /tenants/:tenantId/members* carries
 * the tenant in the URL and mounts TenantParamGuard, so users.manage resolves
 * against the caller's role in that tenant. TenantsSettings already gates its
 * global half internally on isSuperAdmin, so a tenant admin sees only the
 * members panel for the tenants they belong to.
 */
export function configureTabAccess({ can, isSuperAdmin, featureOn }: TabAccess): Record<ConfigTab, boolean> {
  return {
    general: true,
    users: isSuperAdmin,
    roles: isSuperAdmin,
    tenants: isSuperAdmin || can(PERMISSIONS.usersManage),
    // Partners (Customers until z8nrz7earp) are tenant-scoped, so this is
    // `can(...)`. Read opens the tab; CustomersSettings gates its own controls
    // on manage.
    partners: can(PERMISSIONS.customersRead),
    // User groups are tenant-scoped (TenantContextGuard), so a tenant admin
    // holding users.manage there manages them — no superadmin needed.
    'user-groups': can(PERMISSIONS.usersManage),
    // Option sets are part of the `authoring` module, so the tab needs the
    // feature as well as the permission — otherwise it renders a form whose
    // every save 403s. /configure itself stays ungated: it is the shell where
    // an admin turns features back ON, so it must never be hidden by one.
    'option-sets': can(PERMISSIONS.optionSetsManage) && featureOn(FEATURES.authoring),
    'feature-flags': isSuperAdmin,
    // Products has no backend RBAC permission yet (net-new admin surface) —
    // superadmin-only for now, until a tenant-scoped permission exists to
    // delegate it.
    products: isSuperAdmin,
    // Product Codes (z8nrz7fvhx) are tenant-scoped, so this is `can(...)`.
    // Read opens the tab; ProductCodesSettings gates its controls on manage.
    'product-code': can(PERMISSIONS.productCodesRead),
    'din-configure': isSuperAdmin,
    // LIS stages are tenant-scoped and every route the tab calls carries the
    // tenant guard, so this gates on `can(...)`. Read opens the tab; the
    // controls inside need integrations.manage, which the API enforces.
    'lis-stages': can(PERMISSIONS.integrationsRead),
    language: true,
    // Method is a tenant-scoped surface: every route its controls call carries
    // the tenant guard. READ is the gate for the tab itself — the grids render
    // read-only without the manage permission, and MethodSettings checks that
    // separately for its controls.
    method: can(PERMISSIONS.resourceCapabilitiesRead),
    // The tenant's PO template (z8nrz7f7dz). Every route the editor calls is
    // tenant-scoped and writes need purchase_orders.manage, so that is the
    // gate — a reader would only get a form whose every save 403s.
    'purchase-orders': can(PERMISSIONS.purchaseOrdersManage),
    // /sso/setup is users.manage in the selected tenant; provider CRUD inside
    // the tab is superadmin-only, gated there.
    sso: can(PERMISSIONS.usersManage),
  }
}

// ─── Inventory ───────────────────────────────────────────────────────────────

/**
 * In tab-bar order. The /inventory route guard is the gate for every tab but
 * Tasks, which has one of its own (`inventoryTabAccess`).
 */
export const INVENTORY_TABS = [
  { value: 'consumables', label: 'Consumables' },
  { value: 'reagents', label: 'Reagents' },
  { value: 'kits', label: 'Kits' },
  // Tasks (z8nrz7fjzc): the Cleaning / Equipment Tasks tab, for storage units
  // and for consumables, reagents and kits. Sits before Pending Inventory.
  { value: 'tasks', label: 'Tasks' },
  // I13: which received material is not cleared yet — a material connected to
  // an Inventory procedure sits here from receipt (quarantine) through the
  // procedure's forms (in_process) until release, when the row disappears on
  // its own. Not a material-type filter, same as Kits/Storage.
  { value: 'pending', label: 'Pending Inventory' },
  // Storage is the recursive location hierarchy, rooted at Purpose=Storage
  // equipment units — not a material-type filter. Browsing and setting up the
  // shelves are two modes inside this one tab, not two tabs.
  { value: 'storage', label: 'Storage' },
  // Materials at or below their reorder point, out of stock, or expired
  // (z8nrz7f7dz). A worklist across every type, not a material-type filter.
  { value: 'reorder', label: 'Re-Order' },
  { value: 'master-config', label: 'Master Data Config' },
] as const satisfies readonly PageTab[]

export type InventoryTab = (typeof INVENTORY_TABS)[number]['value']

/**
 * Tasks is the equipment task list, so it needs what the Equipment page needs:
 * the equipment feature and equipment.read. Without them it would render a list
 * whose every request 403s.
 */
export function inventoryTabAccess({ can, featureOn }: TabAccess): Record<InventoryTab, boolean> {
  return {
    consumables: true,
    reagents: true,
    kits: true,
    pending: true,
    storage: true,
    tasks: can(PERMISSIONS.equipmentRead) && featureOn(FEATURES.equipment),
    reorder: true,
    'master-config': true,
  }
}

// ─── Products ────────────────────────────────────────────────────────────────

/** In tab-bar order. Ungated, like Inventory: the route guard is the gate. */
export const PRODUCTS_TABS = [
  { value: 'storage', label: 'Storage' },
  { value: 'products', label: 'Products' },
  { value: 'labels', label: 'Labels' },
] as const satisfies readonly PageTab[]

export type ProductTab = (typeof PRODUCTS_TABS)[number]['value']

// ─── Registry ────────────────────────────────────────────────────────────────

type PageTabsDef = {
  tabs: readonly PageTab[]
  /** Absent: every tab opens for whoever may open the page. */
  access?: (access: TabAccess) => Record<string, boolean>
}

/** Keyed by route path, as a Home tile's `to` names it. */
const PAGE_TABS: Record<string, PageTabsDef> = {
  '/configure': { tabs: CONFIGURE_TABS, access: configureTabAccess },
  '/inventory': { tabs: INVENTORY_TABS, access: inventoryTabAccess },
  '/products': { tabs: PRODUCTS_TABS },
}

/** The tabs of `path` the user may open, in tab-bar order. Empty for a page without tabs. */
export function allowedTabsFor(path: string, access: TabAccess): PageTab[] {
  const def = PAGE_TABS[path]
  if (!def) return []
  const allowed = def.access?.(access)
  return def.tabs.filter((tab) => !allowed || allowed[tab.value] === true)
}
